import { Policy } from '../types/policy';

export const policies: Record<'privacy' | 'terms' | 'delivery' | 'refund', Policy> = {
  privacy: {
    title: 'Privacy Policy',
    summary: 'How Tracker Group LLC collects, uses, and protects personal information.',
    sections: [
      {
        id: 'who-we-are',
        heading: 'Who we are',
        paragraphs: [
          'Tracker Group LLC ("we", "us") creates and sells digital educational resources. This policy explains how we handle personal information when you visit this website or purchase our products.',
        ],
      },
      {
        id: 'information-we-collect',
        heading: 'Information we collect',
        paragraphs: ['Depending on how you interact with us, we may collect:'],
        list: [
          'Contact details you send us, such as your name and email address.',
          'Order information needed to deliver your purchase, such as your name and email address. Card payments are handled by a secure third-party payment processor; we never receive or store full card details.',
          'Basic technical data, such as browser type and IP address, that may be logged automatically by our hosting provider. This website does not use analytics or advertising cookies. Our product sales pages may use Meta Pixel to measure advertising performance.',
        ],
      },
      {
        id: 'how-we-use',
        heading: 'How we use information',
        list: [
          'To deliver digital products and provide access.',
          'To respond to questions and support requests.',
          'To process refunds in line with our Refund Policy.',
          'To meet legal and accounting obligations.',
        ],
      },
      {
        id: 'sharing',
        heading: 'Sharing',
        paragraphs: [
          'We do not sell personal information. We share information only with service providers needed to operate our business, such as our payment processor, email delivery service, and website hosting provider, or when required by law.',
        ],
      },
      {
        id: 'retention',
        heading: 'Retention',
        paragraphs: ['We keep personal information only as long as needed to deliver purchases, provide support, and meet legal and accounting requirements.'],
      },
      {
        id: 'your-rights',
        heading: 'Your rights',
        paragraphs: [
          'You may request access to, correction of, or deletion of your personal information by contacting us at soporteoficial.tracker@gmail.com. We will respond within the time required by applicable law.',
        ],
      },
      {
        id: 'children',
        heading: 'Children',
        paragraphs: ['Our website and products are not directed to children under 13, and we do not knowingly collect their information.'],
      },
      {
        id: 'changes',
        heading: 'Changes to this policy',
        paragraphs: ['We may update this policy. The "Last updated" date at the top of this page shows when it last changed.'],
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    summary: 'The terms that apply when you use this website or purchase our digital products.',
    sections: [
      {
        id: 'acceptance',
        heading: 'Acceptance of terms',
        paragraphs: ['By using this website or purchasing a product from Tracker Group LLC, you agree to these terms. If you do not agree, please do not use the site or our products.'],
      },
      {
        id: 'products',
        heading: 'Digital products',
        paragraphs: [
          'All products are digital and delivered electronically. No physical items are shipped. Details about delivery are in our Digital Delivery Policy.',
        ],
      },
      {
        id: 'license',
        heading: 'License to use',
        paragraphs: ['When you purchase a product, you receive a limited, non-exclusive, non-transferable license to use it personally in your own coaching work with your athletes.'],
        list: [
          'You may not resell, redistribute, or publicly share product files.',
          'You may not remove copyright or ownership notices.',
        ],
      },
      {
        id: 'purchases',
        heading: 'Purchases and payment',
        paragraphs: ["Purchases are completed through the secure card checkout on each product's sales page. Prices are shown on each sales page before you pay."],
      },
      {
        id: 'intellectual-property',
        heading: 'Intellectual property',
        paragraphs: ['All content on this website and in our products is owned by Tracker Group LLC or its licensors and is protected by applicable intellectual property laws.'],
      },
      {
        id: 'disclaimer',
        heading: 'Disclaimer',
        paragraphs: ['Our resources are provided for educational purposes "as is". We do not guarantee any specific results from using them.'],
      },
      {
        id: 'liability',
        heading: 'Limitation of liability',
        paragraphs: ['To the extent permitted by law, Tracker Group LLC is not liable for indirect or consequential damages arising from use of the website or products. Our total liability is limited to the amount you paid for the product in question.'],
      },
      {
        id: 'governing-law',
        heading: 'Governing law',
        paragraphs: ['These terms are governed by the laws of the State of Wyoming, United States.'],
      },
      {
        id: 'changes',
        heading: 'Changes to these terms',
        paragraphs: ['We may update these terms from time to time. Continued use of the site after changes means you accept the updated terms.'],
      },
    ],
  },
  delivery: {
    title: 'Digital Delivery Policy',
    summary: 'How and when you receive the digital products you purchase.',
    sections: [
      {
        id: 'no-shipping',
        heading: 'No physical shipping',
        paragraphs: ['Every Tracker Group LLC product is a digital resource. Nothing is shipped by mail, and no shipping fees apply.'],
      },
      {
        id: 'how-delivery-works',
        heading: 'How delivery works',
        paragraphs: ['After your payment is confirmed, your product is sent immediately by email to the address you entered at checkout.'],
      },
      {
        id: 'access-period',
        heading: 'Access period',
        paragraphs: ['Once downloaded, your files are yours to keep and use under the license described in our Terms of Service. We recommend saving them to your own device right after purchase.'],
      },
      {
        id: 'requirements',
        heading: 'Technical requirements',
        paragraphs: ['You need an internet connection and a device able to open PDF files. Please check the product sales page for format details before purchasing.'],
      },
      {
        id: 'problems',
        heading: 'If you have not received your product',
        list: [
          'Check your spam or promotions folder for the delivery email.',
          'Confirm the email address used at checkout.',
          'Contact us at soporteoficial.tracker@gmail.com with your order number and we will help resolve the issue.',
        ],
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    summary: 'When and how refunds are available for digital purchases.',
    sections: [
      {
        id: 'digital-goods',
        heading: 'Digital goods',
        paragraphs: ['Because our products are delivered digitally and can be accessed immediately, refunds are handled according to the conditions below.'],
      },
      {
        id: 'eligibility',
        heading: 'Eligibility',
        paragraphs: ['You may request a refund within 7 days of your purchase date if:'],
        list: [
          "The content you received does not match what is described on the product's sales page.",
          'The product you received does not correspond to the product you purchased.',
          'Requests made after 7 days, or for reasons other than those listed above, are not eligible for a refund.',
        ],
      },
      {
        id: 'how-to-request',
        heading: 'How to request a refund',
        paragraphs: ['Email soporteoficial.tracker@gmail.com with your order number, the product name, and the reason for your request.'],
      },
      {
        id: 'processing',
        heading: 'Processing',
        paragraphs: [
          'We review each request and reply by email. Once a refund is approved, it is processed within 48 to 72 hours and returned to the original payment method. Depending on your bank, it may take a few additional business days to appear on your statement.',
          'Access to the product may be revoked once the refund is processed.',
        ],
      },
    ],
  },
};
